/**
 * AltitudeIndicator — visualizes item altitude (scale 1–5) as rising terrain bars.
 * Altitude determines comparability: items only rank against others at the same altitude.
 *
 * Usage:
 *   <AltitudeIndicator level={3} />
 *   <AltitudeIndicator level={5} showLabel={false} />
 *   <AltitudeIndicator level={1} category="problem" />   // "1 · Bug" (the number, then the name as a tag)
 */

import { useAltitudeText } from '../../hooks/prioritization/useAltitudeText';

const ALTITUDE_COLORS: Record<number, string> = {
  1: 'bg-granite-gray-300',
  2: 'bg-primary-300',
  3: 'bg-primary-500',
  4: 'bg-primary-600',
  5: 'bg-electric-mustard-600',
};

const ALTITUDE_TEXT_COLORS: Record<number, string> = {
  1: 'text-[var(--text-ter)]',
  2: 'text-primary-500',
  3: 'text-primary-600',
  4: 'text-primary-700',
  5: 'text-[var(--mustard)]',
};

interface AltitudeIndicatorProps {
  level: number;
  /** Item category — the name shown is the one the altitude picker shows for this category. */
  category?: string | null;
  max?: number;
  showLabel?: boolean;
  className?: string;
}

export function AltitudeIndicator({
  level,
  category,
  max = 5,
  showLabel = true,
  className = '',
}: AltitudeIndicatorProps) {
  const clampedLevel = Math.max(1, Math.min(level, max));
  const barColor = ALTITUDE_COLORS[clampedLevel] ?? 'bg-primary-500';
  const textColor = ALTITUDE_TEXT_COLORS[clampedLevel] ?? 'text-[var(--text-ter)]';
  const altitudeText = useAltitudeText();
  const label = altitudeText.format(clampedLevel, category, { short: true });

  return (
    <div className={`inline-flex items-center gap-1.5 ${className}`} title={altitudeText.format(clampedLevel, category) ?? `Altitude ${clampedLevel}`}>
      {/* Terrain bars — increasing height like a bar chart */}
      <div className="flex items-end gap-0.5" aria-hidden="true">
        {Array.from({ length: max }).map((_, i) => {
          const barIndex = i + 1;
          const height = 4 + barIndex * 2.5; // 6.5px → 17px
          const filled = barIndex <= clampedLevel;
          return (
            <div
              key={i}
              style={{ height: `${height}px`, width: '3.5px' }}
              className={`rounded-[1px] transition-colors duration-200 ${
                filled ? barColor : 'bg-[var(--hover-tint)]'
              }`}
            />
          );
        })}
      </div>
      {showLabel && (
        <span className={`text-xs font-medium ${textColor}`}>{label}</span>
      )}
    </div>
  );
}
